// @ts-nocheck
import React, { useState } from 'react';
import { Button, InputGroup, Spinner, Checkbox, Icon } from '@blueprintjs/core';
import { useLocation } from 'react-router-dom';
import { useCatalogItemSelection } from './CatalogItemSelectionProvider';
import CatalogCredentialsMissing from './CatalogCredentialsMissing';
import CatalogCredentialsDialog from './CatalogCredentialsDialog';
import '@/style/containers/CatalogItemSelectionDialog/style.scss';
import { useGetPresignedUrlImage } from '@/hooks/query';

const isDirectImageUrl = (value?: string | null) => {
  if (!value) return false;
  const v = String(value).trim().toLowerCase();
  return (
    v.startsWith('http://') ||
    v.startsWith('https://') ||
    v.startsWith('data:') ||
    v.startsWith('blob:')
  );
};

function PresignedImage({ imageKey, alt, className }) {
  const { data, isLoading, isError } = useGetPresignedUrlImage(imageKey, {
    enabled: !!imageKey,
  });
  const url = data?.presigned_url ?? null;

  if (isLoading) return <Spinner />;
  if (isError || !url) return <NoImagePlaceholder />;

  return <img src={url} alt={alt} className={className} />;
}

const NoImagePlaceholder = () => (
  <div className="catalog-selection__product-img-placeholder">
    <svg width="96" height="96" viewBox="0 0 96 96" fill="none" xmlns="http://www.w3.org/2000/svg">
      <rect x="8" y="16" width="80" height="64" rx="4" stroke="#8F99A8" strokeWidth="3" fill="none" />
      <circle cx="30" cy="35" r="7" stroke="#8F99A8" strokeWidth="3" fill="none" />
      <path d="M8 68L28 48L42 62L58 44L88 68" stroke="#8F99A8" strokeWidth="3" strokeLinejoin="round" />
    </svg>
    <p className="carousel__placeholder-text">No image available</p>
  </div>
);

function ProductCard({ product, onAdd, isAdded, isAdding, shouldPresignImages = false }) {
  const isDirectUrl = isDirectImageUrl(product.image);
  const needsPresign = shouldPresignImages && !!product.image && !isDirectUrl;
  const showPlaceholder = !product.image;
  const disabled = !!isAdded || !!isAdding;
  return (
    <div className="catalog-selection__product-card">
      {product.badge && (
        <span className={`catalog-selection__badge catalog-selection__badge--${product.badge.type === 'sponsored' ? 'sponsored' : 'preferred'}`}>
          {product.badge.text}
        </span>
      )}
      {
        product.type === 'inventory' && (
          <span className='catalog-selection__badge catalog-selection__badge--preferred'>
            Inventory
          </span>
        )
      }
      {showPlaceholder ? (
        <NoImagePlaceholder />
      ) : needsPresign ? (
        <PresignedImage
          imageKey={product.image}
          alt={product.name}
          className="catalog-selection__product-img"
        />
      ) : (
        <img
          src={product.image}
          alt={product.name}
          className="catalog-selection__product-img"
        />
      )}
      <div className="catalog-selection__product-info">
        <div className="catalog-selection__product-name">{product.name}</div>
        <div className="catalog-selection__product-supplier">{product.supplier?.name}</div>
        <div className="catalog-selection__product-footer">
          <span className="catalog-selection__product-price">${product.cost}</span>
          <Button className={`catalog-selection__product-btn ${
            isAdded ? 'catalog-selection__product-btn--added' : 'catalog-selection__product-btn--add'
          }`} minimal onClick={() => onAdd(product)} disabled={disabled}>
            Add
          </Button>
        </div>
      </div>
    </div>
  );
}

function FilterSidebar() {
  const { providerConfig, filters, handleFiltersChange } = useCatalogItemSelection();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});

  return (
    <aside className="catalog-selection__sidebar">
      <div className="catalog-selection__sidebar-title">Filter</div>
      {providerConfig.filterSections?.map((section) => (
        <div key={section.key} className="catalog-selection__filter-section">
          {section.type === 'checkbox' ? (
            <Checkbox
              label={section.label}
              checked={!!filters[section.key]}
              onChange={(e) => handleFiltersChange({ [section.key]: e.target.checked })}
            />
          ) : (
            <button
              className="catalog-selection__filter-expand"
              onClick={() => setExpanded((e) => ({ ...e, [section.key]: !e[section.key] }))}
            >
              <span>{section.label}</span>
              <span className="catalog-selection__filter-chevron">{expanded[section.key] ? '▲' : '▼'}</span>
            </button>
          )}
        </div>
      ))}
    </aside>
  );
}

function SelectedItemCard({ item, onRemove, shouldPresignImages = false }) {
  const isDirectUrl = isDirectImageUrl(item.image);
  const needsPresign = shouldPresignImages && !!item.image && !isDirectUrl;
  const showPlaceholder = !item.image;
  return (
    <div className="catalog-selection__selected-card">
      {showPlaceholder ? (
        <NoImagePlaceholder />
      ) : needsPresign ? (
        <div className="catalog-selection__selected-card-images">
          <PresignedImage imageKey={item.image} alt="Catalog Item" />
          <span className="catalog-selection__selected-card-qty">1</span>
        </div>
      ) : (
          <div className="catalog-selection__selected-card-images">
              <img src={item.image} alt="Catalog Item" />
            <span className="catalog-selection__selected-card-qty">1</span>
          </div>
        )
      }
      <div className="catalog-selection__product-info">
        <div className="catalog-selection__product-name">{item.name}</div>
        <div className="catalog-selection__product-supplier">{item.supplier?.name}</div>
        <div className="catalog-selection__product-footer">
          <span className="catalog-selection__product-price">${item.cost}</span>
          <Button minimal intent="danger" icon="trash" onClick={() => onRemove(item.id)} />
        </div>
      </div>
    </div>
  );
}

export default function CatalogItemSelectionContent() {
  const { pathname } = useLocation();
  const {
    provider,
    providerConfig,
    allProviders,
    selectedItems,
    searchQuery,
    hasCredentials,
    usersLoading,
    products,
    productsLoading,
    isConfirming,
    addingItemIds,
    handleProviderChange,
    handleAddItem,
    handleRemoveItem,
    handleSearchChange,
    handleConfirm,
    handleCancel,
  } = useCatalogItemSelection();

  const [providerMenuOpen, setProviderMenuOpen] = useState(false);
  const [tab, setTab] = useState<'search' | 'selected'>('search');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const shouldPresignImages = providerConfig?.key === 'products';
  const isPresentationPage = /^\/presentations\/\d+/i.test(pathname);
  const targetLabel = isPresentationPage ? 'presentation' : 'order';
  const confirmLabel = isPresentationPage ? 'Add To Presentation' : 'Add To Order';

  return (
    <div className="catalog-selection">
      {/* Header */}
      <div className="catalog-selection__header">
        <div className="catalog-selection__header-left">
          {providerConfig.logo ? (
            <img src={providerConfig.logo} alt={providerConfig.label} className={`catalog-selection__provider-logo ${tab === 'search' ? 'catalog-selection__provider-logo--active' : ''}`} onClick={() => setTab('search')} style={{ cursor: 'pointer' }} />
          ) : (
            <div onClick={() => setTab('search')} className={`catalog-selection__provider-label ${tab === 'search' ? 'catalog-selection__provider-label--active' : ''}`}>
              {providerConfig.label}
            </div>
          )}
          <button
            className={`catalog-selection__tab${tab === 'selected' ? ' catalog-selection__tab--active' : ''}`}
            onClick={() => setTab('selected')}
          >
            <Icon icon="tick-circle" />
            Selected Items
            <div className="catalog-selection__selected-count">{selectedItems.length}</div>
          </button>
        </div>

        {/* Provider switcher */}
        <div className="catalog-selection__provider-switcher">
          <Button
            className="catalog-selection__provider-btn"
            onClick={() => setProviderMenuOpen((v) => !v)}
            rightIcon="caret-down"
          >
            {providerConfig.label}
          </Button>
          {providerMenuOpen && (
            <div className="catalog-selection__provider-menu">
              {allProviders.map((p) => !p.hidden && (
                <Button
                  key={p.key}
                  className="catalog-selection__provider-menu-item"
                  onClick={() => { handleProviderChange(p.key); setProviderMenuOpen(false); }}
                >
                  {p.label}
                </Button>
              ))}
            </div>
          )}
        </div>
      </div>

      {tab === 'selected' ? (
        <>
          <div className="catalog-selection__body catalog-selection__body--selected">
            <div className="catalog-selection__selected-grid">
              {selectedItems.map((item) => (
                <SelectedItemCard
                  key={item.id}
                  item={item}
                  onRemove={handleRemoveItem}
                  shouldPresignImages={shouldPresignImages}
                />
              ))}
            </div>
            {
              selectedItems.length === 0 && (
                <div className='no-items-selected'>
                  <h2>There are no selected products to add to the {targetLabel}</h2>
                </div>
              )
            }
          </div>
          <div className="catalog-selection__footer">
            <Button outlined intent="danger" disabled={selectedItems.length === 0} onClick={handleCancel}>Clear All Items</Button>
            <Button className="review-selected-item-btn" loading={isConfirming} disabled={selectedItems.length === 0 || isConfirming} onClick={handleConfirm}>{confirmLabel}</Button>
          </div>
        </>
      ) : (
        <>
          {/* Body */}
          <div className="catalog-selection__body">
            {sidebarOpen && <FilterSidebar />}
            <div className="catalog-selection__main">
              <div className="catalog-selection__search-bar">
                <Button icon="filter" minimal className="catalog-selection__filter-toggle" onClick={() => setSidebarOpen((v) => !v)}>
                  Filter
                </Button>
                <InputGroup
                  leftIcon="search"
                  placeholder="Search Product"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="catalog-selection__search-input"
                />
              </div>
              {usersLoading ? (
                <div className="catalog-selection__loading"><Spinner /></div>
              ) : !hasCredentials ? (
                <CatalogCredentialsMissing />
              ) : productsLoading ? (
                <div className="catalog-selection__loading"><Spinner /></div>
              ) : (
                <div className="catalog-selection__product-grid">
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      onAdd={handleAddItem}
                      isAdded={selectedItems.some((s) => s.id === product.id)}
                      isAdding={!!addingItemIds?.[String(product.id)]}
                      shouldPresignImages={shouldPresignImages}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="catalog-selection__footer">
            <Button outlined intent="danger" onClick={handleCancel}>Cancel</Button>
            <Button className="review-selected-item-btn" disabled={selectedItems.length === 0} onClick={() => setTab('selected')}>
              Review Selected Items
            </Button>
          </div>
        </>
      )}

      <CatalogCredentialsDialog />
    </div>
  );
}
